import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { ArrowDownToLine, ArrowUpFromLine, BarChart2, Gift } from 'lucide-react';
import { useAppSelector } from 'store/hooks';
import { userService } from 'components/api/users';

const formatCurrencyAmount = (amount: string | number, currencyCode = 'USD') => {
  const num = typeof amount === 'number' ? amount : parseFloat(amount || '0');
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      useGrouping: true,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)} ${currencyCode}`;
  }
};

interface BonusOverviewData {
  summary?: {
    totalCount?: number;
    byCurrency?: Array<{
      currencyCode: string;
      count: number;
      amount: string;
    }>;
  };
  claimable?: {
    bonusDefinitions?: unknown[];
    claims?: unknown[];
  };
  history?: {
    totalDocs?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
}

interface PaymentSummaryData {
  deposits?: string | number;
  withdrawals?: string | number;
  profit?: string | number;
  currency?: string;
}

const UserStatistics: React.FC = () => {
  const { userDetail } = useAppSelector((state) => state.users);
  const username = userDetail?.username || 'User';
  const userId = userDetail?.id || 1;

  // Payment summary state
  const [paymentData, setPaymentData] = useState<PaymentSummaryData | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(true);

  // Bonus overview state
  const [bonusData, setBonusData] = useState<BonusOverviewData | null>(null);
  const [bonusLoading, setBonusLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setPaymentLoading(true);
      setBonusLoading(true);

      try {
        const [paymentRes, bonusRes] = await Promise.allSettled([
          userService.getUserPaymentSummary(userId),
          userService.getUserBonusOverview(userId),
        ]);

        if (isMounted) {
          if (paymentRes.status === 'fulfilled') {
            setPaymentData(paymentRes.value?.data || paymentRes.value);
          } else {
            console.error('Failed to fetch payment summary:', paymentRes.reason);
          }

          if (bonusRes.status === 'fulfilled') {
            setBonusData(bonusRes.value?.data || bonusRes.value);
          } else {
            console.error('Failed to fetch bonus overview:', bonusRes.reason);
          }
        }
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
      } finally {
        if (isMounted) {
          setPaymentLoading(false);
          setBonusLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  // Payment stats calculations
  const currency = paymentData?.currency || 'USD';
  const depositAmount = parseFloat(String(paymentData?.deposits ?? 0));
  const withdrawAmount = parseFloat(String(paymentData?.withdrawals ?? 0));
  const profitAmount = parseFloat(String(paymentData?.profit ?? 0));

  // Bonus stats calculations
  const bonusSummary = bonusData?.summary;
  const totalBonusCount = bonusSummary?.totalCount ?? 0;
  const primaryCurrencySummary = bonusSummary?.byCurrency?.[0];

  const totalBonusAmount = primaryCurrencySummary?.amount
    ? formatCurrencyAmount(primaryCurrencySummary.amount, primaryCurrencySummary.currencyCode)
    : '$0.00';

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Title */}
      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, textAlign: 'left' }}>
        Financial statistics for user - {username}
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* Left Column for Deposit & Withdraw */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 0 260px' }}>
          {/* Deposit Block */}
          <Box
            sx={{
              bgcolor: 'var(--bg-panel)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'var(--bg-panel-secondary, rgba(255, 255, 255, 0.05))',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ArrowDownToLine size={20} color="#00e676" />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Total Deposit
              </Typography>
              {paymentLoading ? (
                <Skeleton
                  variant="text"
                  width={100}
                  height={32}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
              ) : (
                <Typography
                  sx={{ color: 'var(--text-primary, white)', fontSize: '1.25rem', fontWeight: 700 }}
                >
                  {formatCurrencyAmount(depositAmount, currency)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Withdraw Block */}
          <Box
            sx={{
              bgcolor: 'var(--bg-panel)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'var(--bg-panel-secondary, rgba(255, 255, 255, 0.05))',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ArrowUpFromLine size={20} color="#ff4d4f" />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Total Withdraw
              </Typography>
              {paymentLoading ? (
                <Skeleton
                  variant="text"
                  width={100}
                  height={32}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
              ) : (
                <Typography
                  sx={{ color: 'var(--text-primary, white)', fontSize: '1.25rem', fontWeight: 700 }}
                >
                  {formatCurrencyAmount(withdrawAmount, currency)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Middle Column for User Profit (vertically centered, large) */}
        <Box
          sx={{
            bgcolor: 'var(--bg-panel)',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flex: '0 0 280px',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'var(--bg-panel-secondary, rgba(255, 255, 255, 0.05))',
              borderRadius: 3,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <BarChart2
              size={28}
              color={
                profitAmount > 0
                  ? 'var(--accent-blue)'
                  : profitAmount < 0
                    ? '#ff4d4f'
                    : 'var(--text-primary)'
              }
            />
          </Box>
          <Box>
            <Typography
              sx={{
                color: 'var(--text-secondary)',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              User Profit
            </Typography>
            {paymentLoading ? (
              <Skeleton
                variant="text"
                width={120}
                height={40}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
              />
            ) : (
              <Typography
                sx={{
                  color:
                    profitAmount > 0
                      ? 'var(--accent-blue)'
                      : profitAmount < 0
                        ? '#ff4d4f'
                        : 'var(--text-primary, white)',
                  fontSize: '2rem',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {formatCurrencyAmount(profitAmount, currency)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right Column for Bonuses */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '0 0 260px' }}>
          {/* Total Bonus Amount Block */}
          <Box
            sx={{
              bgcolor: 'var(--bg-panel)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'var(--bg-panel-secondary, rgba(255, 255, 255, 0.05))',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Gift size={22} color="#00e676" />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Total Bonus Amount
              </Typography>
              {bonusLoading && !bonusData ? (
                <Skeleton
                  variant="text"
                  width={100}
                  height={32}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
              ) : (
                <Typography sx={{ color: '#00e676', fontSize: '1.25rem', fontWeight: 700 }}>
                  {totalBonusAmount}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Total Bonuses Count Block */}
          <Box
            sx={{
              bgcolor: 'var(--bg-panel)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'var(--bg-panel-secondary, rgba(255, 255, 255, 0.05))',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Gift size={22} color="var(--accent-blue)" />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Total Bonuses Count
              </Typography>
              {bonusLoading && !bonusData ? (
                <Skeleton
                  variant="text"
                  width={80}
                  height={32}
                  sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                />
              ) : (
                <Typography
                  sx={{ color: 'var(--text-primary, white)', fontSize: '1.25rem', fontWeight: 700 }}
                >
                  {totalBonusCount}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserStatistics;
